<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { ArrowLeftRight } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';

const props = defineProps({ url: { type: String, required: true } });
const { t } = useI18n();
const expanded = ref(false);
const docked = ref(false);
const side = ref('left');
const x = ref(12);
const y = ref(0);
let timer;
let pointerId = null;
let startX = 0;
let startY = 0;
let originX = 0;
let originY = 0;
let dragged = false;
let suppressClick = false;

const bubbleWidth = computed(() => expanded.value ? 176 : 58);
const positionStyle = computed(() => ({ left: `${x.value}px`, top: `${y.value}px` }));

function clampPosition() {
  x.value = Math.max(-10, Math.min(window.innerWidth - bubbleWidth.value + 10, x.value));
  y.value = Math.max(12, Math.min(window.innerHeight - 70, y.value));
}

function dock() {
  expanded.value = false;
  docked.value = true;
  side.value = x.value + bubbleWidth.value / 2 < window.innerWidth / 2 ? 'left' : 'right';
  x.value = side.value === 'left' ? -10 : window.innerWidth - 48;
  clampPosition();
}

function scheduleDock() {
  clearTimeout(timer);
  timer = setTimeout(dock, 5_000);
}

function activate() {
  if (suppressClick) return;
  if (expanded.value) {
    window.location.assign(props.url);
    return;
  }
  docked.value = false;
  expanded.value = true;
  x.value = side.value === 'right' ? window.innerWidth - bubbleWidth.value - 12 : 12;
  clampPosition();
  scheduleDock();
}

function pointerDown(event) {
  if (pointerId !== null) return;
  pointerId = event.pointerId;
  startX = event.clientX;
  startY = event.clientY;
  originX = x.value;
  originY = y.value;
  dragged = false;
  clearTimeout(timer);
  event.currentTarget.setPointerCapture?.(event.pointerId);
}

function pointerMove(event) {
  if (event.pointerId !== pointerId) return;
  const deltaX = event.clientX - startX;
  const deltaY = event.clientY - startY;
  if (Math.hypot(deltaX, deltaY) > 6) dragged = true;
  if (!dragged) return;
  docked.value = false;
  x.value = originX + deltaX;
  y.value = originY + deltaY;
  clampPosition();
}

function pointerUp(event) {
  if (event.pointerId !== pointerId) return;
  event.currentTarget.releasePointerCapture?.(event.pointerId);
  pointerId = null;
  if (dragged) {
    suppressClick = true;
    setTimeout(() => { suppressClick = false; }, 0);
  }
  scheduleDock();
}

function resize() {
  clampPosition();
  if (docked.value) dock();
}

onMounted(() => {
  y.value = Math.max(90, Math.round(window.innerHeight * .52));
  window.addEventListener('resize', resize);
  scheduleDock();
});

onBeforeUnmount(() => {
  clearTimeout(timer);
  window.removeEventListener('resize', resize);
});
</script>

<template>
  <button
    type="button"
    class="sell-return-bubble"
    :class="{ expanded, docked, right: side === 'right' }"
    :style="positionStyle"
    :aria-label="t('platform.returnSell')"
    @click="activate"
    @pointerdown="pointerDown"
    @pointermove.prevent="pointerMove"
    @pointerup="pointerUp"
    @pointercancel="pointerUp"
  >
    <span class="sell-return-bubble__mark"><ArrowLeftRight :size="17" /></span>
    <span class="sell-return-bubble__label">{{ expanded ? t('platform.returnSell') : 'Sell' }}</span>
  </button>
</template>

<style scoped>
.sell-return-bubble { position: fixed; z-index: 1200; width: 58px; height: 58px; padding: 0 10px; display: flex; align-items: center; justify-content: center; gap: 7px; overflow: hidden; border: 1px solid rgba(255,255,255,.18); border-radius: 999px; color: #fff; background: linear-gradient(145deg,#745fff,#4b35df); box-shadow: 0 14px 32px rgba(42,27,156,.42), inset 0 1px rgba(255,255,255,.18); cursor: grab; touch-action: none; user-select: none; transition: width .24s ease, left .32s cubic-bezier(.2,.8,.2,1), transform .18s ease, box-shadow .18s ease; }
.sell-return-bubble:hover { box-shadow: 0 16px 38px rgba(55,38,187,.52), inset 0 1px rgba(255,255,255,.22); }
.sell-return-bubble:active { cursor: grabbing; transform: scale(.97); }
.sell-return-bubble.expanded { width: 176px; justify-content: flex-start; padding: 0 17px; }
.sell-return-bubble.docked { opacity: .92; }
.sell-return-bubble__mark { display: grid; flex: 0 0 22px; place-items: center; }
.sell-return-bubble__label { overflow: hidden; font-size: 12px; font-weight: 800; letter-spacing: .01em; text-overflow: ellipsis; white-space: nowrap; }
.sell-return-bubble:not(.expanded) .sell-return-bubble__mark { display: none; }
@media (prefers-reduced-motion: reduce) { .sell-return-bubble { transition: none; } }
</style>
